import { useEffect, useState } from 'react';
import useDisclosure, {
  UseDisclosureInterface,
} from '../../../../hooks/useDisclosure';
import { ModalWrapper } from '../../../../hooks/useModal';
import useToast from '../../../../hooks/useToast';
import {
  Box,
  Button,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ControlledDatepicker from '@components/form/controlled/controlled-datepicker';
import moment, { Moment } from 'moment';
import ControlledCheckbox from '@components/form/controlled/controlled-checkbox';
import { FileOpen, Receipt, ReceiptTwoTone } from '@mui/icons-material';
import useAppContext from '@hooks/useAppContext';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useLazyGetYReadingQuery,
  useLazyGetZReadingQuery,
  useLazyGetEJournalQuery,
} from '@api/pos/pos-bo-reports.api.rtk';
import { PrinterDetails } from '@modules/point-of-sale/interface/bo-interface/printers.interface';
import { useAppSelector } from '@store/index';
import { selectPOSPrinterMasterlist } from '@modules/point-of-sale/slice/pos-selector';
import ControlledAutocomplete from '@components/form/controlled/controlled-autocomplete';
import {
  downloadEJournalTxt,
  generateInvoiceTxt,
  generateOrderSlipTxt,
  generateZReadingTxt,
  generateXReadingTxt,
  generateVoidOrderSlipTxt,
  generateVoidInvoiceTxt,
} from '@utils/printer-format/pos/pos-e-journal.format';
import {
  InvoiceJsonData,
  OrderSlipJsonData,
  VoidInvoiceJsonData,
  VoidOrderSlipJsonData,
  XReadingJsonData,
  ZReadingJsonData,
} from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import DialogMessage from '@components/dialog/dialog-message';

const yzReadingSchema = yup.object().shape({
  menu: yup.string().default('').required().label('Menu'),
  dateFrom: yup
    .date()
    .default(() => moment().toDate())
    .required(),
  dateTo: yup
    .date()
    .default(() => moment().add(1, 'day').toDate())
    .required()
    .label('Date End'),
  zReadingStart: yup.boolean().default(false).required().label('Menu'),
});

type YZReadingFormValues = yup.InferType<typeof yzReadingSchema>;

const POSYZReadingModal = ({
  modalDisclosure,
}: {
  referenceId: string;
  modalDisclosure: UseDisclosureInterface;
}) => {
  const messageDialog = useDisclosure();

  const {
    // startLoadingStatus,
    stopLoadingStatus,
  } = useAppContext();
  const [printer, setPrinter] = useState<PrinterDetails | null>(null);
  const posPrinters = useAppSelector(selectPOSPrinterMasterlist);
  const [yReading, { isError: isYReadingError, error: yReadingError }] =
    useLazyGetYReadingQuery();
  const [zReading, { isError: isZReadingError, error: zReadingError }] =
    useLazyGetZReadingQuery();
  const [eJournal, { isError: isEJournalError, error: eJournalError }] =
    useLazyGetEJournalQuery();
  const {
    formState: {
      // errors
    },
    watch,
    handleSubmit,
    // reset,
    setValue,
  } = useForm<YZReadingFormValues>({
    defaultValues: {
      dateFrom: new Date(),
      dateTo: new Date(),
      menu: 'Y-Reading',
      zReadingStart: false,
    },
    resolver: yupResolver(yzReadingSchema),
    mode: 'onSubmit',
  });

  const [dateFrom, setDateFrom] = useState<Moment | null>(moment(new Date()));
  const [dateTo, setDateTo] = useState<Moment | null>(moment(new Date()));
  const [zReadingStart, setZReadingStart] = useState(false);
  const { openSecCodeDialog } = useAppContext();
  const [selected, setSelected] = useState('Y-Reading');
  const smallScreen = useMediaQuery('(max-width: 990px)');

  useEffect(() => {
    if (!posPrinters || posPrinters.length === 0) return;
    setPrinter(posPrinters[0]);
  }, [posPrinters]);

  const handleChange = (_: React.MouseEvent<HTMLElement>, value: string) => {
    if (value !== null) setSelected(value);
    setValue('menu', selected);
  };

  useEffect(() => {
    if (selected !== 'Z-Reading') {
      setZReadingStart(false);
    }
  }, [selected]);

  const { toast } = useToast();

  useEffect(() => {
    if (!isEJournalError) return;
  }, [eJournalError]);

  useEffect(() => {
    setValue('dateFrom', moment(dateFrom).toDate());
    setValue('dateTo', moment(dateTo).toDate());
    setValue('menu', selected);
    setValue('zReadingStart', zReadingStart);
  }, [dateFrom, dateTo, selected, zReadingStart]);

  const cardValue = [
    {
      title: 'Y-Reading',
      icon: <Receipt sx={{ fontSize: 24 }} />,
      description:
        'Y Reading is the summary of sales per shift on selected dates.',
    },
    {
      title: 'Z-Reading',
      icon: <ReceiptTwoTone sx={{ fontSize: 24 }} />,
      description:
        'Z-Reading is the end-of-day or periodic summary of sales transactions and related financial activities.',
    },
    {
      title: 'E-Journal',
      icon: <FileOpen sx={{ fontSize: 24 }} />,
      description:
        'The Electronic Sales Journal (e-journal) is a report required by the Bureau of Internal Revenue (BIR) to be available in the POS machine.',
    },
  ];

  const printReceipt: SubmitHandler<YZReadingFormValues> = async (formData) => {
    if (!watch('dateFrom') || !watch('dateTo')) {
      return toast({
        severity: 'warning',
        message: 'Please double check date start and date end.',
      });
    }
    try {
      if (formData.menu === 'Y-Reading') {
        openSecCodeDialog(async (res) => {
          if (!res?.data.isValid) return;
          try {
            await yReading({
              dateStart: moment(formData.dateFrom).format('YYYY-MM-DD'),
              dateEnd: moment(formData.dateTo).format('YYYY-MM-DD'),
              printerRefID: printer?.printerID || '',
            }).unwrap();
            if (isYReadingError) {
              toast({
                severity: 'error',
                message: yReadingError,
              });
              return;
            } else {
              toast({
                severity: 'success',
                message: 'Y Reading Report printed successfully',
              });
            }
          } catch (err) {
            toast({
              severity: 'error',
              message: err,
            });
          }
        }, 'access_pos_module');
      } else if (formData.menu === 'Z-Reading') {
        openSecCodeDialog(async (res) => {
          if (!res?.data.isValid) return;
          try {
            await zReading({
              dateStart: moment(formData.dateFrom).format('YYYY-MM-DD'),
              dateEnd: moment(formData.dateFrom).format('YYYY-MM-DD'),
              printerRefID: printer?.printerID || '',
            }).unwrap();
            if (isZReadingError) {
              toast({
                severity: 'error',
                message: zReadingError,
              });
              return;
            } else {
              toast({
                severity: 'success',
                message: 'Z Reading Report reprinted successfully',
              });
            }
          } catch (err) {
            toast({
              severity: 'error',
              message: err,
            });
          }
        }, 'access_pos_module');
      } else {
        openSecCodeDialog(async (res) => {
          if (!res?.data.isValid) return;
          await eJournal({
            dateStart: moment(formData.dateFrom).format('YYYY-MM-DD'),
            dateEnd: moment(formData.dateTo).format('YYYY-MM-DD'),
          })
            .unwrap()
            .then(async (res) => {
              const printData = res?.printData?.[0];
              const ejournals = printData?.ejournal;
              const header = printData?.header;

              if (!Array.isArray(ejournals)) return;

              // startLoadingStatus('Generating E-Journal');

              let txt = ''; // accumulator

              const lineLength =
                printer?.lineLength ?? posPrinters[0].lineLength;

              let counter = 1;

              for (const item of ejournals) {
                const data = item?.json_data;
                const type = item?.print_type;

                if (!data || !type) continue;

                if (type === 'OS') {
                  const osTxt = generateOrderSlipTxt(
                    data as OrderSlipJsonData, // ✅ narrow type
                    lineLength,
                  );
                  txt += osTxt + '\n\n';
                } else if (type === 'VoidOS') {
                  const voidOrderSlipTxt = generateVoidOrderSlipTxt(
                    data as VoidOrderSlipJsonData,
                    lineLength,
                  );
                  txt += voidOrderSlipTxt + '\n\n';
                } else if (type === 'Invoices') {
                  const invoiceTxt = generateInvoiceTxt(
                    data as InvoiceJsonData,
                    header,
                    lineLength,
                  );
                  txt += invoiceTxt + '\n\n';
                } else if (type === 'VoidInvoices') {
                  const invoiceTxt = generateVoidInvoiceTxt(
                    data as VoidInvoiceJsonData,
                    header,
                    lineLength,
                  );
                  txt += invoiceTxt + '\n\n';
                } else if (type === 'XReading') {
                  const xReadingTxt = generateXReadingTxt(
                    data as XReadingJsonData,
                    header,
                    lineLength,
                  );
                  txt += xReadingTxt + '\n\n';
                } else if (type === 'ZReading') {
                  const zReadingTxt = generateZReadingTxt(
                    data as ZReadingJsonData,
                    header,
                    lineLength,
                  );
                  txt += zReadingTxt + '\n\n';
                }

                counter++;
              }
              downloadEJournalTxt(txt);
              setTimeout(() => messageDialog.onOpen(), 200);
            })
            .catch((err) => {
              toast({ severity: 'error', message: err });
            })
            .finally(() => stopLoadingStatus());
        }, 'access_pos_module');
      }
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    } finally {
      stopLoadingStatus();
    }
  };

  return (
    <>
      <ModalWrapper
        {...{
          modalDisclosure,
          disableOnClose: false,
          withHeader: {
            title: 'Y/Z Reading',
            learnMore: () => {},
            onClose: modalDisclosure.onClose,
          },
        }}
      >
        <Stack component={'form'} gap={2} onSubmit={handleSubmit(printReceipt)}>
          <Stack
            direction={smallScreen ? 'column' : 'row'}
            justifyContent='space-between'
            gap={smallScreen ? 2 : 1}
          >
            <ControlledAutocomplete
              options={posPrinters}
              getOptionLabel={(option) =>
                `${option?.prefix} - ${option?.logicalDeviceName}`
              }
              selectedValue={printer}
              setSelectedValue={setPrinter}
              label='Reprint Where'
              size='small'
              sx={{
                minWidth: 200,
                '& .MuiInputBase-root': {
                  border: '',
                },
                '& .MuiOutlinedInput-root': {
                  '& > fieldset': {
                    borderColor: '',
                  },
                },
                flex: 1,
              }}
              disableClearable
            />

            <Stack
              direction='row'
              flexWrap='wrap'
              alignItems='center'
              gap={smallScreen ? 2 : 1}
              width='100%'
            >
              <ControlledDatepicker
                selectedDate={moment(dateFrom)}
                setSelectedDate={setDateFrom}
                label='From'
                clearable={false}
                maxDate={moment(dateTo)}
                disabled={zReadingStart}
                sx={{
                  '& .MuiInputBase-input': {
                    pl: '8px',
                  },
                  flex: 1,
                  minWidth: 200,
                }}
              />

              <ControlledDatepicker
                selectedDate={moment(dateTo)}
                setSelectedDate={setDateTo}
                clearable={false}
                label='To'
                minDate={moment(dateFrom)}
                sx={{
                  '& .MuiInputBase-input': {
                    pl: '8px',
                  },
                  flex: 1,
                  minWidth: 200,
                }}
                disabled={selected === 'Z-Reading'} // ✅ disable when Z-Reading
              />
            </Stack>

            <label htmlFor='zReadingStart'>
              <Stack
                sx={{
                  width: smallScreen ? '100%' : 'max-content',
                  justifySelf: 'flex-end',
                  pl: smallScreen ? 1 : 2,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, background-color 0.2s',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: '#e0e0e0',
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 500,
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ControlledCheckbox
                  id='zReadingStart'
                  name='zReadingStart'
                  checkedValue={zReadingStart}
                  setCheckedValue={setZReadingStart}
                  label='Z - Reading from Start'
                  disabled={selected !== 'Z-Reading'}
                />
              </Stack>
            </label>
          </Stack>

          {smallScreen && <Divider sx={{ mb: -1 }} />}

          {/* Buttons */}
          <Stack py={smallScreen ? 1 : 2}>
            <ToggleButtonGroup
              value={selected}
              exclusive
              color='primary'
              onChange={handleChange}
              sx={{
                flexDirection: smallScreen ? 'column' : 'row',
                width: '100%',
                gap: 1.5,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  gap: 2,
                  position: 'relative',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  p: 2,
                  border: '1px solid',
                  borderColor: 'lightGrey',
                  borderRadius: 2,
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&.Mui-selected': {
                    borderColor: 'primary.main',
                    backgroundColor: '#e5e6f2',
                  },
                  '&:hover': {
                    borderColor: 'gray.main',
                  },
                },
              }}
            >
              {cardValue.map(({ title, icon, description }) => {
                const isSelected = selected === title;
                return (
                  <ToggleButton
                    key={title}
                    value={title}
                    sx={{ minHeight: '90px' }}
                  >
                    {/* radio indicator */}
                    <Stack
                      position='absolute'
                      top={8}
                      right={8}
                      width={16}
                      height={16}
                      border='2px solid'
                      borderColor={isSelected ? 'primary.main' : 'GrayText'}
                      borderRadius='50%'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            width: 9.3,
                            height: 9.3,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                      )}
                    </Stack>

                    <Typography>{icon}</Typography>
                    <Stack direction='column' textAlign='left' gap={1}>
                      <Typography
                        variant='body1'
                        color='ButtonText'
                        fontWeight={500}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant='caption'
                        textTransform='initial'
                        maxWidth={smallScreen ? '350px' : '200px'}
                        color='GrayText'
                      >
                        {description}
                      </Typography>
                    </Stack>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Stack>

          <Button
            variant='contained'
            color='primary'
            size='large'
            type='submit'
            sx={{
              alignSelf: smallScreen ? 'center' : 'flex-end',
              width: smallScreen ? '100%' : 'max-content',
            }}
          >
            {selected === 'E-Journal' ? 'Generate' : 'Print Receipt'}
          </Button>
        </Stack>
      </ModalWrapper>

      <DialogMessage
        isOpen={messageDialog.isOpen}
        error={null}
        onClose={messageDialog.onClose}
        message={{
          title: 'Success',
          desc: `The ${selected} downloaded successfully.`,
        }}
      />
    </>
  );
};

export default POSYZReadingModal;
